'use client';

import React, { useState, useEffect } from 'react';
import Webcam from 'react-webcam';
import { Button } from './ui/button';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "./ui/dialog";
import { VisuallyHidden } from "./ui/visually-hidden";

import { dataURLtoFile } from './utils';
import { ImagePrompt, CapturedImage } from '../types/types';
import { getResolvedSilhouetteUrl } from './utils/silhouetteUtils';

interface CameraModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File | File[] | CapturedImage[]) => void;
  silhouette?: string;
    multiCapture?: boolean;
    maxImages?: number;
    existingImages?: File[] | CapturedImage[];
    prompts?: ImagePrompt[]; // For sequential prompt-based capture
    specificPromptId?: string; // For re-capturing a specific prompt
    captureDelay?: number; // Delay in ms between captures (default: 1500ms)
}

// Use React.forwardRef to allow the parent to pass a ref to the <Webcam> component
const CameraModal = React.forwardRef<Webcam, CameraModalProps>(
  ({ isOpen, onClose, onCapture, silhouette, multiCapture = false, maxImages = 10, existingImages = [], prompts, specificPromptId, captureDelay = 1500 }, ref) => {
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [showSilhouette, setShowSilhouette] = useState(true);
    const [silhouetteVisible, setSilhouetteVisible] = useState(false);
    const [isCameraReady, setIsCameraReady] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [capturedPreviewUrl, setCapturedPreviewUrl] = useState<string | null>(null);

    // Determine if we're using prompts mode
    const hasPrompts = prompts && prompts.length > 0;
    const isReCapture = !!specificPromptId;

    // For prompts mode, track current prompt index
    const [currentPromptIndex, setCurrentPromptIndex] = useState(0);

    // Determine current prompt - use useMemo to recalculate when dependencies change
    const currentPrompt = React.useMemo(() => {
      if (isReCapture && hasPrompts && prompts) {
        const found = prompts.find(p => p.id === specificPromptId);
        return found;
      }
      if (hasPrompts && prompts) {
        const current = prompts[currentPromptIndex];
        return current;
      }
      return null;
    }, [prompts, currentPromptIndex, isReCapture, specificPromptId, hasPrompts]);

    useEffect(() => {
        // Determine which silhouette to validate (prompt-specific or generic)
        const activeSilhouette = currentPrompt?.silhouetteUrl || silhouette;
        const resolvedSilhouetteUrl = getResolvedSilhouetteUrl(activeSilhouette);

        console.log('[SILHOUETTE] Validation triggered:', {
            isOpen,
            promptLabel: currentPrompt?.label || 'No prompt',
            promptSilhouette: currentPrompt?.silhouetteUrl,
            genericSilhouette: silhouette,
            activeSilhouette,
            resolvedUrl: resolvedSilhouetteUrl
        });

        if (isOpen && resolvedSilhouetteUrl) {
            const img = new Image();
            img.src = resolvedSilhouetteUrl;
            img.onload = () => {
                console.log('[SILHOUETTE] Image loaded successfully:', resolvedSilhouetteUrl);
                setSilhouetteVisible(true);
            };
            img.onerror = () => {
                console.error('[SILHOUETTE] Image failed to load:', resolvedSilhouetteUrl);
                setSilhouetteVisible(false);
            };
        } else if (!isOpen) {
            console.log('[SILHOUETTE] Modal closed, resetting silhouette state');
            setIsCameraReady(false);
            setSilhouetteVisible(false);
            setShowSilhouette(true); // Reset showSilhouette when modal closes
        } else {
            console.log('[SILHOUETTE] No silhouette URL available');
            setSilhouetteVisible(false);
        }
    }, [isOpen, silhouette, currentPrompt]);

        // State for captured images - supports both legacy File[] and new CapturedImage[]
        const [capturedImages, setCapturedImages] = useState<File[] | CapturedImage[]>(
          Array.isArray(existingImages) ? existingImages : []
        );
        const [isMobile, setIsMobile] = useState(false);
        const [isLandscape, setIsLandscape] = useState(false);
        const [facingMode, setFacingMode] = useState<"user" | "environment">("environment");

        // Mobile detection and orientation handling
        React.useEffect(() => {
            const checkMobile = () => {
                setIsMobile(
                    window.innerWidth < 640 ||
                        (window.innerWidth < 940 && window.innerHeight < 450)
                );
                setIsLandscape(window.innerWidth > window.innerHeight);
            };
            checkMobile();
            window.addEventListener("resize", checkMobile);
            return () => window.removeEventListener("resize", checkMobile);
        }, []);

        // Reset captured images when modal opens
        // Only run when isOpen changes to prevent infinite loops from prompts/existingImages changing
        const prevIsOpen = React.useRef(isOpen);
        React.useEffect(() => {
            if (isOpen && !prevIsOpen.current) {
                // Modal just opened
                setCapturedImages(Array.isArray(existingImages) ? existingImages : []);

                // If re-capturing a specific prompt, set the index to that prompt
                if (specificPromptId && prompts && prompts.length > 0) {
                    const promptIndex = prompts.findIndex(p => p.id === specificPromptId);
                    setCurrentPromptIndex(promptIndex >= 0 ? promptIndex : 0);
                } else {
                    setCurrentPromptIndex(0);
                }
            }
            prevIsOpen.current = isOpen;
        }, [isOpen, existingImages, specificPromptId, prompts]);

        const handleModalCapture = () => {
            // Prevent multiple rapid captures
            if (isProcessing) return;

            setIsProcessing(true);

            const webcamRef = ref as React.RefObject<Webcam>;
            if (webcamRef.current) {
                const imageSrc = webcamRef.current.getScreenshot();
                if (imageSrc) {
                    const file = dataURLtoFile(
                        imageSrc,
                        `capture_${Date.now()}.jpeg`
                    );
                    if (file) {
                        if (hasPrompts && currentPrompt) {
                            // Prompts mode: create CapturedImage object
                            const capturedImage: CapturedImage = {
                                promptId: currentPrompt.id,
                                promptLabel: currentPrompt.label,
                                file: file,
                            };

                            // Calculate the updated images array
                            const updatedImages = (() => {
                                const filtered = (capturedImages as CapturedImage[]).filter(img => img.promptId !== currentPrompt.id);
                                return [...filtered, capturedImage];
                            })();

                            // Update state
                            setCapturedImages(updatedImages);

                            // Auto-advance to next prompt if not re-capturing
                            // Use configurable delay to allow camera to stabilize and image to render
                            if (!isReCapture && currentPromptIndex < prompts.length - 1) {
                                // Not the last prompt - advance to next
                                setTimeout(() => {
                                    setCurrentPromptIndex(prev => prev + 1);
                                    setIsProcessing(false);
                                }, captureDelay);
                            } else if (isReCapture) {
                                // Re-capture mode - close with delay and pass updated images
                                setTimeout(() => {
                                    handleDone(updatedImages);
                                }, captureDelay);
                            } else if (!isReCapture && currentPromptIndex === prompts.length - 1) {
                                // Last prompt in sequential mode - auto-close with delay and pass updated images
                                setTimeout(() => {
                                    handleDone(updatedImages);
                                }, captureDelay);
                            }
                        } else {
                            // Legacy mode: append File to array
                            const updatedImages = multiCapture
                                ? [...(capturedImages as File[]), file]
                                : [file];

                            setCapturedImages(updatedImages);
                            if (!multiCapture) {
                                setCapturedPreviewUrl(imageSrc);
                            }
                            setIsProcessing(false);
                        }
                    } else {
                        setIsProcessing(false);
                    }
                } else {
                    setIsProcessing(false);
                }
            } else {
                setIsProcessing(false);
            }
        };

        const handleDone = (imagesToSubmit?: File[] | CapturedImage[]) => {
            // Use passed images or fall back to state
            const images = imagesToSubmit || capturedImages;

            if (images.length > 0) {
                if (hasPrompts) {
                    // Prompts mode: return CapturedImage[]
                    onCapture(images as CapturedImage[]);
                } else if (multiCapture) {
                    // Multi-capture: return array of all images
                    onCapture(images as File[]);
                } else {
                    // Single-capture: return single file
                    onCapture((images as File[])[0]);
                }
            }
            setIsProcessing(false);
            onClose();
        };

        const handleCameraError = () => {
            console.error("Camera error occurred");
            setCameraError(
                "Unable to access camera. Please check settings and permissions."
            );
        };

        const toggleCamera = () => {
            setFacingMode(prev => prev === "environment" ? "user" : "environment");
        };

    if (!isOpen) return null;

        const atMaxImages = multiCapture && capturedImages.length >= maxImages;

        // Desktop view - standard modal dialog
        if (!isMobile) {
            return (
                <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
                    <DialogContent
                        className="max-w-[95vw] sm:max-w-2xl lg:max-w-3xl p-0 flex flex-col max-h-[95vh]"
                        onPointerDownOutside={(e) => e.preventDefault()}
                        onInteractOutside={(e) => e.preventDefault()}
                    >
                    <DialogHeader className="p-4 border-b flex-shrink-0">
                        <DialogTitle>
                            {hasPrompts && currentPrompt
                                ? isReCapture
                                    ? `Capture: ${currentPrompt.label}`
                                    : `${currentPromptIndex + 1}/${prompts.length}: ${currentPrompt.label}`
                                : multiCapture
                                    ? `Take Photos (${capturedImages.length}/${maxImages})`
                                    : "Take Photo"
                            }
                        </DialogTitle>
                    </DialogHeader>
                    <div className="p-4 flex-grow overflow-y-auto space-y-4 relative">
                        {/* Prompt badge overlay for desktop */}
                        {hasPrompts && currentPrompt && (
                            <div className="absolute top-6 left-1/2 -translate-x-1/2 z-20 bg-black/80 text-white px-6 py-3 rounded-lg shadow-lg">
                                <div className="text-xs font-semibold opacity-80 text-center mb-1">
                                    {isReCapture ? "Re-capture" : `Image ${currentPromptIndex + 1} of ${prompts.length}`}
                                </div>
                                <div className="text-lg font-bold text-center">
                                    {currentPrompt.label}
                                </div>
                            </div>
                        )}

                        {(() => {
                          const activeSilhouette = currentPrompt?.silhouetteUrl || silhouette;
                          const resolvedSilhouetteUrl = getResolvedSilhouetteUrl(activeSilhouette);
                          const shouldDisplay = resolvedSilhouetteUrl && showSilhouette && silhouetteVisible && isCameraReady;

                          console.log('[SILHOUETTE] Desktop display check:', {
                            resolvedUrl: resolvedSilhouetteUrl,
                            showSilhouette,
                            silhouetteVisible,
                            isCameraReady,
                            willDisplay: shouldDisplay
                          });

                          return shouldDisplay && (
                            <img
                              src={resolvedSilhouetteUrl}
                              alt="Silhouette"
                              className="absolute inset-0 w-[85%] mx-auto h-full object-contain opacity-40 pointer-events-none"
                            />
                          );
                        })()}
                        {cameraError ? (
                            <p className="text-red-500 text-center">
                                {cameraError}
                            </p>
                        ) : capturedPreviewUrl && !multiCapture && !hasPrompts ? (
                            <img src={capturedPreviewUrl} alt="Captured photo" className="rounded-md border w-full" />
                        ) : (
                            <Webcam
                                audio={false}
                                ref={ref}
                                screenshotFormat="image/jpeg"
                                width="100%"
                                className="rounded-md border"
                                videoConstraints={{
                                    facingMode: { ideal: facingMode },
                                    width: { ideal: 1280 },
                                    height: { ideal: 720 },
                                }}
                                onUserMediaError={handleCameraError}
                                onUserMedia={() => setTimeout(() => setIsCameraReady(true), 10)}
                                playsInline
                            />
                        )}

                    </div>
                    <DialogFooter className="p-4 border-t flex justify-between">
                        <Button variant="outline" onClick={onClose} size="lg" className="h-12">
                            Cancel
                        </Button>
                        <div className="flex gap-2">
                            {silhouette && silhouetteVisible && (
                                <Button variant="outline" onClick={() => setShowSilhouette(!showSilhouette)} size="lg" className="h-12">
                                    {showSilhouette ? 'Hide' : 'Show'} Silhouette
                                </Button>
                            )}
                            {!cameraError && (
                                <>
                                    {hasPrompts ? (
                                        // Prompts mode - sequential capture
                                        <>
                                            <Button onClick={handleModalCapture} size="lg" className="h-12" disabled={isProcessing}>
                                                {isProcessing ? (
                                                    <>
                                                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    isReCapture ? "Capture" : `Capture ${currentPrompt?.label || ""}`
                                                )}
                                            </Button>
                                        </>
                                    ) : multiCapture ? (
                                        // Multi-capture mode (legacy)
                                        <>
                                            {!atMaxImages && (
                                                <Button onClick={handleModalCapture} size="lg" className="h-12" disabled={isProcessing}>
                                                    {isProcessing ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Processing...
                                                        </>
                                                    ) : (
                                                        "Capture Another"
                                                    )}
                                                </Button>
                                            )}
                                            {capturedImages.length > 0 && (
                                                <Button onClick={() => handleDone()} variant="default" size="lg" className="h-12" disabled={isProcessing}>
                                                    Done ({capturedImages.length})
                                                </Button>
                                            )}
                                        </>
                                    ) : (
                                        // Single-capture mode
                                        <>
                                            {capturedImages.length === 0 ? (
                                                <Button onClick={handleModalCapture} size="lg" className="h-12" disabled={isProcessing}>
                                                    {isProcessing ? (
                                                        <>
                                                            <svg className="animate-spin -ml-1 mr-2 h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                            Processing...
                                                        </>
                                                    ) : (
                                                        "Capture Photo"
                                                    )}
                                                </Button>
                                            ) : (
                                                <>
                                                    <Button onClick={() => { setCapturedPreviewUrl(null); setCapturedImages([]); }} variant="outline" size="lg" className="h-12">
                                                        Retake
                                                    </Button>
                                                    <Button onClick={() => handleDone()} variant="default" size="lg" className="h-12" disabled={isProcessing}>
                                                        Done
                                                    </Button>
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            );
        }

        // Mobile view - full-screen immersive experience
        return (
            <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
                <DialogContent
                    className="flex flex-col w-full h-full p-0 fixed inset-0 bg-black z-50 !translate-x-0 !translate-y-0 !max-w-none"
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                >
                    {/* Hidden title for accessibility */}
                    <VisuallyHidden>
                        <DialogTitle>
                            {hasPrompts && currentPrompt
                                ? isReCapture
                                    ? `Capture: ${currentPrompt.label}`
                                    : `${currentPromptIndex + 1}/${prompts.length}: ${currentPrompt.label}`
                                : multiCapture
                                    ? `Take Photos (${capturedImages.length}/${maxImages})`
                                    : "Take Photo"
                            }
                        </DialogTitle>
                    </VisuallyHidden>

                    {/* Mobile camera view */}
                    <div className="relative w-full h-full flex flex-col">
                        {/* Cancel button - top left corner to prevent accidental taps */}
                        <Button
                            className="absolute top-4 left-4 h-10 w-10 rounded-full bg-black/50 text-white hover:bg-black/70 border border-white/30 shadow-lg p-0 flex items-center justify-center z-30"
                            onClick={onClose}
                            type="button"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="w-5 h-5"
                            >
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </Button>

                        {/* Prompt badge for prompts mode - compact badge style */}
                        {hasPrompts && currentPrompt && (
                            <div className="absolute top-4 right-4 bg-black/80 text-white px-3 py-2 rounded-lg shadow-lg z-30 max-w-[200px]">
                                <div className="text-[10px] font-semibold opacity-80 leading-tight">
                                    {isReCapture ? "Re-capture" : `${currentPromptIndex + 1} of ${prompts.length}`}
                                </div>
                                <div className="text-sm font-bold leading-tight mt-0.5">
                                    {currentPrompt.label}
                                </div>
                            </div>
                        )}

                        {/* Webcam fills screen */}
                        <div className="relative flex-grow">
                            {cameraError ? (
                                <div className="flex items-center justify-center h-full">
                                    <p className="text-red-500 text-center px-4">
                                        {cameraError}
                                    </p>
                                </div>
                            ) : capturedPreviewUrl && !multiCapture && !hasPrompts ? (
                                <img src={capturedPreviewUrl} alt="Captured photo" className="w-full h-full object-cover" />
                            ) : (
                                <>
                                    <Webcam
                                        audio={false}
                                        ref={ref}
                                        screenshotFormat="image/jpeg"
                                        className="w-full h-full object-cover"
                                        videoConstraints={{
                                            facingMode: { ideal: facingMode },
                                            width: { ideal: 1280 },
                                            height: { ideal: 720 },
                                        }}
                                        onUserMediaError={handleCameraError}
                                        onUserMedia={() => setTimeout(() => setIsCameraReady(true), 10)}
                                        playsInline
                                    />
                                    {(() => {
                                      const activeSilhouette = currentPrompt?.silhouetteUrl || silhouette;
                                      const resolvedSilhouetteUrl = getResolvedSilhouetteUrl(activeSilhouette);
                                      const shouldDisplay = resolvedSilhouetteUrl && showSilhouette && silhouetteVisible && isCameraReady;

                                      console.log('[SILHOUETTE] Mobile display check:', {
                                        resolvedUrl: resolvedSilhouetteUrl,
                                        showSilhouette,
                                        silhouetteVisible,
                                        isCameraReady,
                                        willDisplay: shouldDisplay
                                      });

                                      return shouldDisplay && (
                                        <img
                                          src={resolvedSilhouetteUrl}
                                          alt="Silhouette"
                                          className="absolute inset-0 w-[85%] mx-auto h-full object-contain opacity-40 pointer-events-none"
                                        />
                                      );
                                    })()}
                                    {/* Camera rotation button - below cancel button (portrait) or top center (landscape) */}
                                    <Button
                                        className={`absolute h-12 w-12 rounded-full bg-black/50 text-white hover:bg-black/70 border border-white/30 shadow-lg p-0 flex items-center justify-center z-10 ${
                                            isLandscape
                                                ? "top-4 left-1/2 -translate-x-1/2"
                                                : "top-16 left-4"
                                        }`}
                                        onClick={toggleCamera}
                                        type="button"
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            className="w-7 h-7"
                                        >
                                            <path d="M21 2v6h-6" />
                                            <path d="M3 12a9 9 0 0 1 15-6.7L21 8" />
                                            <path d="M3 22v-6h6" />
                                            <path d="M21 12a9 9 0 0 1-15 6.7L3 16" />
                                        </svg>
                                    </Button>
                                </>
                            )}
                        </div>

                        {/* Controls */}
                        {isLandscape ? (
                            // Landscape layout: buttons on right
                            <>
                                {/* Floating buttons - right side */}
                                <div className="absolute top-1/2 right-6 transform -translate-y-1/2 flex flex-col space-y-4">
                                    {!hasPrompts && multiCapture && capturedImages.length > 0 && (
                                        <div className="bg-black/70 text-white px-3 py-1 rounded-full text-sm font-medium text-center">
                                            {capturedImages.length}/{maxImages}
                                        </div>
                                    )}
                                    {!cameraError && (
                                        <>
                                            {hasPrompts ? (
                                                // Prompts mode - always show capture button
                                                <Button
                                                    className="rounded-full w-16 h-16 bg-white/80 text-black hover:bg-white shadow-lg flex items-center justify-center"
                                                    onClick={handleModalCapture}
                                                    disabled={isProcessing}
                                                >
                                                    {isProcessing ? (
                                                        <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>
                                                    ) : (
                                                        ""
                                                    )}
                                                </Button>
                                            ) : (
                                                // Legacy mode - only show if not at max and no preview showing
                                                !atMaxImages && !capturedPreviewUrl && (
                                                    <Button
                                                        className="rounded-full w-16 h-16 bg-white/80 text-black hover:bg-white shadow-lg flex items-center justify-center"
                                                        onClick={handleModalCapture}
                                                        disabled={isProcessing}
                                                    >
                                                        {isProcessing ? (
                                                            <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </Button>
                                                )
                                            )}
                                        </>
                                    )}
                                    {!hasPrompts && capturedImages.length > 0 && (
                                        <>
                                            {!multiCapture && (
                                                <Button
                                                    variant="outline"
                                                    className="rounded-full px-6 py-3 shadow-lg bg-black/50 text-white border border-white/30 hover:bg-black/70"
                                                    onClick={() => { setCapturedPreviewUrl(null); setCapturedImages([]); }}
                                                >
                                                    Retake
                                                </Button>
                                            )}
                                            <Button
                                                variant="default"
                                                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-6 py-3 shadow-lg"
                                                onClick={() => handleDone()}
                                            >
                                                Done
                                            </Button>
                                        </>
                                    )}
                                    {silhouette && silhouetteVisible && (
                                        <Button variant="outline" className="bg-black text-white w-fit !mx-auto mt-5" onClick={() => setShowSilhouette(!showSilhouette)}>
                                            {showSilhouette ? 'Hide' : 'Show'} Silhouette
                                        </Button>
                                    )}
                                </div>
                            </>
                        ) : (
                            // Portrait layout: buttons at bottom
                            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent pb-safe">
                                {/* Buttons */}
                                <div className="flex flex-col items-center gap-3 px-4 pb-6">
                                    {!hasPrompts && multiCapture && capturedImages.length > 0 && (
                                        <div className="bg-white/20 text-white px-4 py-1 rounded-full text-sm font-medium">
                                            {capturedImages.length}/{maxImages} images
                                        </div>
                                    )}
                                    <div className="flex items-center gap-4 w-full justify-center">
                                        {!cameraError && (
                                            <>
                                                {hasPrompts ? (
                                                    // Prompts mode - always show capture button
                                                    <Button
                                                        className="rounded-full w-16 h-16 bg-white/80 text-black hover:bg-white shadow-lg flex items-center justify-center"
                                                        onClick={handleModalCapture}
                                                        disabled={isProcessing}
                                                    >
                                                        {isProcessing ? (
                                                            <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                            </svg>
                                                        ) : (
                                                            ""
                                                        )}
                                                    </Button>
                                                ) : (
                                                    // Legacy mode - only show if not at max and no preview showing
                                                    !atMaxImages && !capturedPreviewUrl && (
                                                        <Button
                                                            className="rounded-full w-16 h-16 bg-white/80 text-black hover:bg-white shadow-lg flex items-center justify-center"
                                                            onClick={handleModalCapture}
                                                            disabled={isProcessing}
                                                        >
                                                            {isProcessing ? (
                                                                <svg className="animate-spin h-8 w-8" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                                </svg>
                                                            ) : (
                                                                ""
                                                            )}
                                                        </Button>
                                                    )
                                                )}
                                            </>
                                        )}
                                    </div>
                                    {!hasPrompts && capturedImages.length > 0 && (
                                        <div className="flex gap-3 w-full">
                                            {!multiCapture && (
                                                <Button
                                                    variant="outline"
                                                    className="w-full h-12"
                                                    onClick={() => { setCapturedPreviewUrl(null); setCapturedImages([]); }}
                                                >
                                                    Retake
                                                </Button>
                                            )}
                                            <Button
                                                variant="default"
                                                className="w-full bg-blue-600 hover:bg-blue-700 text-white h-12"
                                                onClick={() => handleDone()}
                                            >
                                                {multiCapture ? `Done (${capturedImages.length})` : 'Done'}
                                            </Button>
                                        </div>
                                    )}
                                    {silhouette && silhouetteVisible && (
                                        <Button variant="outline" className="bg-black text-white w-fit !mx-auto mt-5" onClick={() => setShowSilhouette(!showSilhouette)}>
                                            {showSilhouette ? 'Hide' : 'Show'} Silhouette
                                        </Button>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        );
    }
);

CameraModal.displayName = 'CameraModal'; // Helps in debugging

export { CameraModal };
